import React from 'react';
import type { PatientInfo, ReportSectionData } from '../types';
import {
    DalanyLogo,
    DalanyTextLogo,
    QrCodeIcon,
    DalanyKurdishTextLogo,
} from './icons';


const BarcodeIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg fill="currentColor" viewBox="0 0 200 40" className={className} xmlns="http://www.w3.org/2000/svg">
        <path d="M 10 0 H 12 V 40 H 10 Z" /> <path d="M 16 0 H 18 V 40 H 16 Z" /> <path d="M 20 0 H 24 V 40 H 20 Z" />
        <path d="M 28 0 H 30 V 40 H 28 Z" /> <path d="M 32 0 H 34 V 40 H 32 Z" /> <path d="M 40 0 H 44 V 40 H 40 Z" />
        <path d="M 46 0 H 48 V 40 H 46 Z" /> <path d="M 52 0 H 54 V 40 H 52 Z" /> <path d="M 58 0 H 60 V 40 H 58 Z" />
        <path d="M 64 0 H 66 V 40 H 64 Z" /> <path d="M 70 0 H 74 V 40 H 70 Z" /> <path d="M 78 0 H 80 V 40 H 78 Z" />
        <path d="M 82 0 H 84 V 40 H 82 Z" /> <path d="M 88 0 H 90 V 40 H 88 Z" /> <path d="M 94 0 H 98 V 40 H 94 Z" />
        <path d="M 102 0 H 104 V 40 H 102 Z" /> <path d="M 110 0 H 112 V 40 H 110 Z" /> <path d="M 116 0 H 120 V 40 H 116 Z" />
        <path d="M 124 0 H 126 V 40 H 124 Z" /> <path d="M 130 0 H 132 V 40 H 130 Z" /> <path d="M 136 0 H 138 V 40 H 136 Z" />
        <path d="M 142 0 H 146 V 40 H 142 Z" /> <path d="M 150 0 H 152 V 40 H 150 Z" /> <path d="M 158 0 H 160 V 40 H 158 Z" />
        <path d="M 164 0 H 166 V 40 H 164 Z" /> <path d="M 170 0 H 172 V 40 H 170 Z" /> <path d="M 176 0 H 180 V 40 H 176 Z" />
        <path d="M 184 0 H 186 V 40 H 184 Z" />
    </svg>
);


const MdcReport: React.FC<{ patientInfo: PatientInfo; reportData: ReportSectionData[] }> = ({ patientInfo, reportData }) => {
    
    const parseResult = (value: string | number) => {
        const strValue = String(value);
        const match = strValue.match(/([\d.]+)\s*([↑↓])?/);
        if (match) {
            return {
                number: match[1],
                arrow: match[2] || null,
                isAbnormal: !!match[2]
            };
        }
        return { number: strValue, arrow: null, isAbnormal: false };
    };
    
    return (
        <main className="a4-page-container relative flex flex-col">
            <div className="absolute inset-0 flex items-center justify-center z-0 opacity-5 pointer-events-none">
                <DalanyLogo className="w-96 h-96 text-slate-400" />
            </div>
            <div className="relative z-10 font-sans flex flex-col flex-grow">
                {/* Header */}
                 <header className="flex justify-between items-center pb-4 border-b-4 border-slate-200">
                    {/* Left: Logo + Lab Name */}
                    <div className="flex items-center space-x-4">
                        <DalanyLogo className="h-16 w-16 text-brand" />
                        <div>
                            <DalanyTextLogo className="h-8 text-black" />
                            <p className="text-sm text-slate-500 tracking-wide mt-1">For Pathological Analysis</p>
                        </div>
                    </div>

                    {/* Right: Registration Info */}
                    <div className="text-xs">
                       <div className="grid grid-cols-[max-content,1fr] gap-x-4 gap-y-0.5">
                            <div className="font-semibold text-slate-500 text-right">
                                <p>Registration Number:</p>
                                <p>Request Number:</p>
                                <p>Requested on:</p>
                                <p>Collected on:</p>
                                <p>Reported on:</p>
                            </div>
                            <div className="font-mono font-bold text-slate-700">
                                <p>{patientInfo.registrationNumber}</p>
                                <p>{patientInfo.requestNumber}</p>
                                <p>{patientInfo.requestedOn}</p>
                                <p>{patientInfo.collectedOn}</p>
                                <p>{patientInfo.reportedOn}</p>
                            </div>
                       </div>
                       <div className="mt-2 flex justify-end">
                            <BarcodeIcon className="h-8 w-48 text-black" />
                       </div>
                    </div>
                </header>
                
                {/* Report Title Section */}
                <div className="text-center py-3">
                    <span className="font-rabar text-base text-slate-500 align-middle">ئه نجامی تاقیگه</span>
                    <span className="text-slate-300 mx-3 align-middle font-light">|</span>
                    <h1 className="inline-block text-xl font-bold text-slate-800 tracking-wide align-middle">Laboratory Report</h1>
                    <span className="text-slate-300 mx-3 align-middle font-light">|</span>
                    <span className="font-rabar text-base text-slate-500 align-middle">نتيجة المختبر</span>
                </div>


                {/* Patient Info */}
                <section className="text-sm border-t border-b border-black py-4">
                     <p className="font-bold text-base mb-2 text-slate-900">{patientInfo.name}</p>
                     <div className="grid grid-cols-4 gap-x-4 gap-y-1 text-xs">
                         <div>
                            <span className="font-semibold text-slate-600 mr-2">DOB:</span>
                            <span className="text-slate-800">{patientInfo.dob}</span>
                         </div>
                         <div>
                            <span className="font-semibold text-slate-600 mr-2">Age:</span>
                            <span className="text-slate-800">{patientInfo.age}</span>
                         </div>
                         <div>
                            <span className="font-semibold text-slate-600 mr-2">Gender:</span>
                            <span className="text-slate-800">{patientInfo.gender}</span>
                         </div>
                         <div>
                            <span className="font-semibold text-slate-600 mr-2">Referred by:</span>
                            <span className="font-bold text-slate-800">{patientInfo.referredBy}</span>
                         </div>
                     </div>
                </section>

                {/* Results Table */}
                <div className="flex-grow pt-4">
                    <table className="w-full border-collapse text-sm">
                        <thead className="bg-slate-100 text-slate-600 font-semibold">
                            <tr>
                                <th className="p-2 text-left w-[45%] font-semibold text-xs uppercase tracking-wider">TEST NAME</th>
                                <th className="p-2 text-center w-[20%] font-semibold text-xs uppercase tracking-wider">Result Value</th>
                                <th className="p-2 text-left w-[35%] font-semibold text-xs uppercase tracking-wider">Biological Reference Range</th>
                            </tr>
                        </thead>
                        {reportData.map((section) => (
                            <tbody key={section.title}>
                                <tr>
                                    <td colSpan={3} className="p-2 font-bold text-base bg-slate-200 text-slate-900">{section.title}</td>
                                </tr>
                                {section.results.map((result, index) => {
                                    const { number, arrow, isAbnormal } = parseResult(result.value);
                                    return (
                                        <tr key={index} className="border-b border-slate-200/80 even:bg-slate-50/50">
                                            <td className="p-3 align-top font-medium text-slate-800">{result.name}</td>
                                            <td className={`p-3 text-center align-top font-bold ${isAbnormal ? 'text-red-600' : 'text-slate-800'}`}>
                                                {number} {arrow && <span className="inline-block ml-1">{arrow}</span>}
                                            </td>
                                            <td className="p-3 align-top text-slate-600 whitespace-pre-wrap">{result.normalRange} {result.unit && ` ${result.unit}`}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        ))}
                    </table>
                </div>
                
                {/* Footer */}
                <footer className="mt-auto pt-6 text-xs">
                    {/* Top section: QR, Signature, Page number */}
                    <div className="flex justify-between items-end border-t border-slate-200/80 pt-4 mb-4">
                        <div className="flex items-center gap-3">
                            <QrCodeIcon className="h-12 w-12 text-slate-400" />
                            <div>
                                <p className="font-bold text-slate-700">Scan to Verify Report</p>
                                <p className="text-slate-500 mt-0.5">Verify authenticity online.</p>
                            </div>
                        </div>
                        <div className="page-number font-sans font-semibold text-slate-500"></div>
                        <div className="text-center">
                            <div className="w-40 h-10 border-b-2 border-slate-300 mb-1"></div>
                            <p className="font-semibold text-slate-700">Reviewed By</p>
                        </div>
                    </div>

                    {/* Bottom section: Addresses */}
                    <div className="border-t border-slate-200/80 pt-3 pb-2">
                        <h3 className="text-center text-sm font-rabar-bold text-slate-800 mb-3" dir="rtl">
                            ناونیشانی تاقیگەکانمان
                        </h3>
                        <div className="grid grid-cols-4 gap-x-6 text-[11px] font-rabar">
                            {/* Branch 1 */}
                            <div className="leading-snug text-right">
                                <div className="flex items-center justify-start gap-2" dir="rtl">
                                    <DalanyKurdishTextLogo className="h-3 text-slate-800" />
                                    <span className="font-rabar-bold text-slate-800">٤٠ مەتری</span>
                                </div>
                                <p className="text-slate-500" dir="rtl">هەولێر، شەقامی ٤٠ مەتری، چواریانی کۆک، بەرامبەر باخچەی برایەتی</p>
                                <p className="font-sans font-semibold text-brand pt-0.5" dir="ltr">0750 388 7447</p>
                            </div>
                            {/* Branch 2 */}
                            <div className="leading-snug text-right">
                                <div className="flex items-center justify-start gap-2" dir="rtl">
                                    <DalanyKurdishTextLogo className="h-3 text-slate-800" />
                                    <span className="font-rabar-bold text-slate-800">ڕزگاری</span>
                                </div>
                                <p className="text-slate-500" dir="rtl">هەولێر، شەقامی ١٠٠ مەتری، بەرامبەر نەخۆشخانەی ڕزگاری</p>
                                <p className="font-sans font-semibold text-brand pt-0.5" dir="ltr">0750 519 4008</p>
                            </div>
                            {/* Branch 3 */}
                            <div className="leading-snug text-right">
                                <div className="flex items-center justify-start gap-2" dir="rtl">
                                    <DalanyKurdishTextLogo className="h-3 text-slate-800" />
                                    <span className="font-rabar-bold text-slate-800">خەبات</span>
                                </div>
                                <p className="text-slate-500" dir="rtl">قەزای خەبات، شەقامی ناوبازاڕ، تەنیشت قوتابخانەی ئادۆمەند</p>
                                <p className="font-sans font-semibold text-brand pt-0.5" dir="ltr">0750 603 4008</p>
                            </div>
                            {/* Branch 4 */}
                            <div className="leading-snug text-right">
                                <div className="flex items-center justify-start gap-2" dir="rtl">
                                    <DalanyKurdishTextLogo className="h-3 text-slate-800" />
                                    <span className="font-rabar-bold text-slate-800">ڕانیە</span>
                                </div>
                                <p className="text-slate-500" dir="rtl">قەزای ڕانیە، شەقامی ناو بازاڕ، بەرامبەر مزگەوتی ناحیەخان</p>
                                <p className="font-sans font-semibold text-brand pt-0.5" dir="ltr">0750 214 4008</p>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </main>
    );
};
export default MdcReport;